// frontend/src/samkalp-admin/tabs/QuotaTab.js
//
// Duplicated from frontend/src/pages/AskOSAdmin.js's QuotaTab (not exported there).
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Row, Col, Form, Button, Alert, Badge, Spinner, Modal } from 'react-bootstrap';
import { Users, Zap, BarChart2, Upload, Trash2 } from 'lucide-react';
import api from '../../api/api';
import { EmptyRow, useAlert } from './shared';

const QuotaTab = ({ slug }) => {
    const [view,           setView]          = useState('policies');
    const [defaultPolicy,  setDefaultPolicy] = useState(null);
    const [students,       setStudents]      = useState([]);
    const [analytics,      setAnalytics]     = useState(null);
    const [loading,        setLoading]       = useState(true);
    const [saving,         setSaving]        = useState(false);
    const [editingDefault, setEditingDefault]= useState(false);
    const [editStudent,    setEditStudent]   = useState(null);
    const [defaultForm,    setDefaultForm]   = useState({ daily_limit: '', weekly_limit: '', monthly_limit: '' });
    const [studentForm,    setStudentForm]   = useState({ daily_limit: '', weekly_limit: '', monthly_limit: '', notes: '' });
    const [uploadMsg,      setUploadMsg]     = useState(null);
    const [alert,          showAlert]        = useAlert();

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [defRes, stuRes] = await Promise.all([
                api.get(`/tenant/${slug}/askos/admin/quota/default`),
                api.get(`/tenant/${slug}/askos/admin/quota/students`),
            ]);
            setDefaultPolicy(defRes.data);
            setDefaultForm({
                daily_limit:   defRes.data.daily_limit   != null ? String(defRes.data.daily_limit)   : '',
                weekly_limit:  defRes.data.weekly_limit  != null ? String(defRes.data.weekly_limit)  : '',
                monthly_limit: defRes.data.monthly_limit != null ? String(defRes.data.monthly_limit) : '',
            });
            setStudents(stuRes.data);
        } catch { /* empty */ }
        setLoading(false);
    }, [slug]);

    const loadAnalytics = useCallback(async () => {
        try {
            const res = await api.get(`/tenant/${slug}/askos/admin/quota/analytics?days=30`);
            setAnalytics(res.data);
        } catch { /* empty */ }
    }, [slug]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => { if (view === 'analytics') loadAnalytics(); }, [view, loadAnalytics]);

    const saveDefault = async () => {
        setSaving(true);
        try {
            await api.put(`/tenant/${slug}/askos/admin/quota/default`, {
                daily_limit:   defaultForm.daily_limit   ? parseFloat(defaultForm.daily_limit)   : null,
                weekly_limit:  defaultForm.weekly_limit  ? parseFloat(defaultForm.weekly_limit)  : null,
                monthly_limit: defaultForm.monthly_limit ? parseFloat(defaultForm.monthly_limit) : null,
            });
            showAlert('Default policy saved.');
            setEditingDefault(false);
            loadAll();
        } catch (e) { showAlert(e.response?.data?.detail || 'Save failed.', 'danger'); }
        setSaving(false);
    };

    const saveStudentPolicy = async () => {
        if (!editStudent) return;
        setSaving(true);
        try {
            await api.put(`/tenant/${slug}/askos/admin/quota/${editStudent.student_id}`, {
                daily_limit:   studentForm.daily_limit   ? parseFloat(studentForm.daily_limit)   : null,
                weekly_limit:  studentForm.weekly_limit  ? parseFloat(studentForm.weekly_limit)  : null,
                monthly_limit: studentForm.monthly_limit ? parseFloat(studentForm.monthly_limit) : null,
                notes:         studentForm.notes || null,
            });
            showAlert(`Quota set for ${editStudent.email}.`);
            setEditStudent(null);
            loadAll();
        } catch (e) { showAlert(e.response?.data?.detail || 'Save failed.', 'danger'); }
        setSaving(false);
    };

    const removeStudentPolicy = async (s) => {
        try {
            await api.delete(`/tenant/${slug}/askos/admin/quota/${s.student_id}`);
            showAlert('Override removed — student now follows default policy.', 'warning');
            loadAll();
        } catch (e) { showAlert(e.response?.data?.detail || 'Remove failed.', 'danger'); }
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadMsg(null);
        const fd = new FormData();
        fd.append('file', file);
        try {
            const res = await api.post(`/tenant/${slug}/askos/admin/quota/bulk`, fd);
            setUploadMsg({ type: 'success', text: `Created: ${res.data.created}, Updated: ${res.data.updated}` });
            loadAll();
        } catch (e) { setUploadMsg({ type: 'danger', text: e.response?.data?.detail || 'Upload failed.' }); }
        e.target.value = '';
    };

    const LimitDisplay = ({ v }) =>
        v != null ? <span className="fw-semibold">{Number(v).toFixed(1)} cr</span>
                  : <span className="text-muted x-small">Unlimited</span>;

    const UsageBar = ({ used, limit, pct }) => {
        if (limit == null) return <span className="x-small text-muted">—</span>;
        const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
        return (
            <div>
                <div className="d-flex justify-content-between mb-1">
                    <span className="x-small" style={{ color }}>{Number(used).toFixed(2)} / {Number(limit).toFixed(1)}</span>
                    <span className="x-small text-muted">{Math.round(pct)}%</span>
                </div>
                <div className="rounded-pill overflow-hidden" style={{ height: 4, background: '#e2e8f0' }}>
                    <div style={{ width: `${Math.min(pct, 100)}%`, height: '100%', background: color, borderRadius: 999 }} />
                </div>
            </div>
        );
    };

    const LimitInput = ({ label, field, form, setForm }) => (
        <Form.Group className="mb-3">
            <Form.Label className="x-small fw-bold text-muted text-uppercase">{label}</Form.Label>
            <Form.Control type="number" step="0.01" min="0" placeholder="Leave blank for unlimited"
                value={form[field]} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                className="border-0 border-bottom rounded-0 px-0 shadow-none bg-transparent" />
            <Form.Text className="x-small text-muted">Credits · blank = unlimited</Form.Text>
        </Form.Group>
    );

    return (
        <>
            {alert && <Alert variant={alert.variant} className="py-2 small mb-3" role="status">{alert.msg}</Alert>}

            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex gap-2">
                    <Button size="sm" variant={view === 'policies'  ? 'primary' : 'outline-secondary'}
                        className="rounded-pill d-flex align-items-center gap-1" onClick={() => setView('policies')}>
                        <Zap size={13} />Policies
                    </Button>
                    <Button size="sm" variant={view === 'analytics' ? 'primary' : 'outline-secondary'}
                        className="rounded-pill d-flex align-items-center gap-1" onClick={() => setView('analytics')}>
                        <BarChart2 size={13} />Analytics
                    </Button>
                </div>
                <label className="btn btn-sm btn-outline-secondary rounded-pill d-flex align-items-center gap-1 mb-0"
                    style={{ cursor: 'pointer' }}>
                    <Upload size={13} />Bulk CSV
                    <input type="file" accept=".csv" style={{ display: 'none' }} onChange={handleBulkUpload} />
                </label>
            </div>

            {uploadMsg && <Alert variant={uploadMsg.type} className="py-2 small mb-3" role="status">{uploadMsg.text}</Alert>}

            {view === 'policies' && (
                <>
                    <Card className="faculty-card mb-4">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <div>
                                    <p className="x-small fw-bold text-muted text-uppercase mb-0">
                                        <Zap size={12} className="me-1" />Default Policy
                                    </p>
                                    <p className="x-small text-muted mb-0">Applied to all students without an individual override.</p>
                                </div>
                                <Button size="sm" variant="outline-secondary" className="rounded-pill"
                                    onClick={() => setEditingDefault(e => !e)}>
                                    {editingDefault ? 'Cancel' : 'Edit'}
                                </Button>
                            </div>
                            {!editingDefault ? (
                                <Row className="g-3">
                                    {[
                                        { label: 'Daily Limit',   v: defaultPolicy?.daily_limit   },
                                        { label: 'Weekly Limit',  v: defaultPolicy?.weekly_limit  },
                                        { label: 'Monthly Limit', v: defaultPolicy?.monthly_limit },
                                    ].map(({ label, v }) => (
                                        <Col xs={4} key={label}>
                                            <div className="text-center p-3 rounded-3"
                                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                <p className="x-small text-muted text-uppercase mb-1">{label}</p>
                                                <p className="fw-bold mb-0" style={{ fontSize: '1.1rem' }}>
                                                    {v != null ? `${Number(v).toFixed(1)} cr` : '∞'}
                                                </p>
                                            </div>
                                        </Col>
                                    ))}
                                </Row>
                            ) : (
                                <Row className="g-3 align-items-end">
                                    <Col md={3}><LimitInput label="Daily Limit"   field="daily_limit"   form={defaultForm} setForm={setDefaultForm} /></Col>
                                    <Col md={3}><LimitInput label="Weekly Limit"  field="weekly_limit"  form={defaultForm} setForm={setDefaultForm} /></Col>
                                    <Col md={3}><LimitInput label="Monthly Limit" field="monthly_limit" form={defaultForm} setForm={setDefaultForm} /></Col>
                                    <Col md={3}>
                                        <Button variant="primary" className="w-100 rounded-pill mb-3" onClick={saveDefault} disabled={saving}>
                                            {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : 'Save Default'}
                                        </Button>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    <Card className="faculty-card">
                        <Card.Header className="bg-transparent border-0 pt-3 px-4 pb-0">
                            <p className="x-small fw-bold text-muted text-uppercase mb-0">
                                Student Quotas <Badge bg="secondary" className="ms-2 fw-normal">{students.length}</Badge>
                            </p>
                        </Card.Header>
                        <Table responsive hover className="mb-0 small">
                            <thead className="table-light">
                                <tr>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 px-4">Student</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Daily</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Monthly</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">Today</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3">This Month</th>
                                    <th scope="col" className="x-small fw-bold text-muted text-uppercase py-3 text-end pe-4">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={6} className="text-center py-4"><Spinner animation="border" size="sm" aria-hidden="true" /></td></tr>
                                ) : students.length === 0 ? (
                                    <EmptyRow cols={6} icon={Users} message="No students yet." />
                                ) : students.map(s => (
                                    <tr key={s.student_id}>
                                        <td className="py-3 px-4">
                                            <div className="d-flex align-items-center gap-2">
                                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center fw-bold text-primary flex-shrink-0"
                                                    style={{ width: 32, height: 32, fontSize: '0.75rem' }}>
                                                    {(s.full_name || s.email || '?')[0].toUpperCase()}
                                                </div>
                                                <div>
                                                    <div className="fw-semibold text-dark">{s.full_name || '—'}</div>
                                                    <div className="x-small text-muted">{s.email}</div>
                                                    {s.has_override && (
                                                        <Badge className="badge-soft-primary x-small fw-normal">Override</Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="py-3 align-middle"><LimitDisplay v={s.daily_limit} /></td>
                                        <td className="py-3 align-middle"><LimitDisplay v={s.monthly_limit} /></td>
                                        <td className="py-3 align-middle" style={{ minWidth: 120 }}>
                                            <UsageBar used={s.daily_used}
                                                limit={s.daily_limit ?? defaultPolicy?.daily_limit}
                                                pct={s.daily_limit ? (s.daily_used / s.daily_limit * 100) : 0} />
                                        </td>
                                        <td className="py-3 align-middle" style={{ minWidth: 120 }}>
                                            <UsageBar used={s.monthly_used}
                                                limit={s.monthly_limit ?? defaultPolicy?.monthly_limit}
                                                pct={s.monthly_limit ? (s.monthly_used / s.monthly_limit * 100) : 0} />
                                        </td>
                                        <td className="py-3 pe-4 align-middle text-end">
                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                <Button size="sm" variant="outline-secondary"
                                                    className="rounded-pill d-flex align-items-center gap-1"
                                                    onClick={() => {
                                                        setEditStudent(s);
                                                        setStudentForm({
                                                            daily_limit:   s.daily_limit   != null ? String(s.daily_limit)   : '',
                                                            weekly_limit:  s.weekly_limit  != null ? String(s.weekly_limit)  : '',
                                                            monthly_limit: s.monthly_limit != null ? String(s.monthly_limit) : '',
                                                            notes: s.notes || '',
                                                        });
                                                    }}>
                                                    <Zap size={12} />Set Quota
                                                </Button>
                                                {s.has_override && (
                                                    <Button size="sm" variant="outline-danger"
                                                        className="rounded-pill d-flex align-items-center gap-1"
                                                        onClick={() => removeStudentPolicy(s)}>
                                                        <Trash2 size={12} />Reset
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Card>
                </>
            )}

            {view === 'analytics' && (
                analytics ? (
                    <Row className="g-4">
                        <Col md={6}>
                            <Card className="faculty-card h-100">
                                <Card.Header className="bg-transparent border-0 pt-3 px-4 pb-0">
                                    <p className="x-small fw-bold text-muted text-uppercase mb-0">Top Consumers — 30 Days</p>
                                </Card.Header>
                                <Card.Body className="p-0">
                                    <Table hover className="mb-0 small">
                                        <thead className="table-light">
                                            <tr>
                                                <th scope="col" className="py-2 px-4 x-small fw-bold text-muted text-uppercase">Student</th>
                                                <th scope="col" className="py-2 x-small fw-bold text-muted text-uppercase">Credits</th>
                                                <th scope="col" className="py-2 x-small fw-bold text-muted text-uppercase">Msgs</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {analytics.top_consumers.map((c, i) => (
                                                <tr key={c.student_id}>
                                                    <td className="py-2 px-4">
                                                        <span className="text-muted me-2 x-small">{i + 1}.</span>{c.name}
                                                    </td>
                                                    <td className="py-2 fw-semibold">{Number(c.total_credits).toFixed(2)}</td>
                                                    <td className="py-2 text-muted">{c.message_count}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="faculty-card h-100">
                                <Card.Header className="bg-transparent border-0 pt-3 px-4 pb-0">
                                    <p className="x-small fw-bold text-muted text-uppercase mb-0">By Platform</p>
                                </Card.Header>
                                <Card.Body className="px-4">
                                    {analytics.platform_breakdown.map(p => (
                                        <div key={p.platform} className="d-flex justify-content-between py-2 border-bottom">
                                            <span className="small text-capitalize">{p.platform}</span>
                                            <div className="text-end">
                                                <div className="fw-semibold small">{Number(p.credits).toFixed(2)} cr</div>
                                                <div className="x-small text-muted">{p.messages} msgs</div>
                                            </div>
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3}>
                            <Card className="faculty-card h-100">
                                <Card.Header className="bg-transparent border-0 pt-3 px-4 pb-0">
                                    <p className="x-small fw-bold text-muted text-uppercase mb-0">Costliest Topics</p>
                                </Card.Header>
                                <Card.Body className="px-4">
                                    {analytics.top_concepts.map(c => (
                                        <div key={c.concept} className="py-2 border-bottom">
                                            <div className="small fw-semibold text-truncate">{c.concept}</div>
                                            <div className="x-small text-muted">{Number(c.credits).toFixed(2)} cr · {c.questions} q</div>
                                        </div>
                                    ))}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                ) : <div className="text-center py-5"><Spinner animation="border" size="sm" aria-hidden="true" /></div>
            )}

            <Modal show={!!editStudent} onHide={() => setEditStudent(null)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold fs-6">Set Quota — {editStudent?.full_name || editStudent?.email}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="small text-muted mb-4">Leave blank to use the default policy for that period.</p>
                    <LimitInput label="Daily Limit (credits)"   field="daily_limit"   form={studentForm} setForm={setStudentForm} />
                    <LimitInput label="Weekly Limit (credits)"  field="weekly_limit"  form={studentForm} setForm={setStudentForm} />
                    <LimitInput label="Monthly Limit (credits)" field="monthly_limit" form={studentForm} setForm={setStudentForm} />
                    <Form.Group>
                        <Form.Label className="x-small fw-bold text-muted text-uppercase">Admin Note (optional)</Form.Label>
                        <Form.Control as="textarea" rows={2} value={studentForm.notes}
                            onChange={e => setStudentForm(f => ({ ...f, notes: e.target.value }))}
                            placeholder="e.g. Research student — extended quota" className="shadow-none" />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button variant="light" onClick={() => setEditStudent(null)}>Cancel</Button>
                    <Button variant="primary" className="rounded-pill px-4" onClick={saveStudentPolicy} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" aria-hidden="true" /> : 'Save Quota'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default QuotaTab;
